import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import Dialog from '@app/src/components/Dialog'
import { useAccount } from '@app/src/context/AccountContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import EditQuestionAnswerClassification from '@app/src/pages/QuestionEditor/AnswerClassification'
import type {
  QuestionAnswerClassificationRuleFormData,
  QuestionNumberClassificationRuleFormData,
  QuestionOptionClassificationRuleFormData,
} from '@app/src/pages/QuestionEditor/AnswerClassification/utils'
import {
  mapToAnswerClassificationDataFormData,
  mapToClassificationSubmitData,
  mapToNumberClassificationDataFormData,
  mapToOptionClassificationDataFormData,
} from '@app/src/pages/QuestionEditor/AnswerClassification/utils'
import type { EditQuestionFormData } from '@app/src/pages/QuestionEditor/EditQuestion'
import type { QuestionAnswerClassificationRule } from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { WF_ORGANIZATION_ID } from '@app/src/wf-constants'
import React, { useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import type { AssessmentPoints, EnhancedAssessmentFormQuestion } from './utils/types'

interface EditQuestionClassificationDialogProps {
  isOpen: boolean
  closeDialog: () => void
  setCurrentQuestion: (question: EnhancedAssessmentFormQuestion | undefined) => void
  currentQuestion: EnhancedAssessmentFormQuestion | undefined
  assessmentType: AssessmentType
  onClassificationSave?: (questionId: number, classificationData: ClassificationRulesFormData) => void
}

export interface ClassificationRulesFormData {
  questionAnswerClassificationRules?: QuestionAnswerClassificationRuleFormData
  questionNumberClassificationRules?: QuestionNumberClassificationRuleFormData
  questionOptionClassificationRules?: QuestionOptionClassificationRuleFormData
  impactValueType?: string
  points?: AssessmentPoints
  requestCollectionId?: number
}

const hasClassificationRulesChanged = (
  original: ClassificationRulesFormData,
  current: ClassificationRulesFormData,
): boolean => {
  return (
    JSON.stringify(original.questionAnswerClassificationRules) !==
      JSON.stringify(current.questionAnswerClassificationRules) ||
    JSON.stringify(original.questionNumberClassificationRules) !==
      JSON.stringify(current.questionNumberClassificationRules) ||
    JSON.stringify(original.questionOptionClassificationRules) !==
      JSON.stringify(current.questionOptionClassificationRules)
  )
}

export const EditQuestionClassificationDialog: React.FC<EditQuestionClassificationDialogProps> = ({
  isOpen,
  closeDialog,
  setCurrentQuestion,
  currentQuestion,
  assessmentType,
  onClassificationSave,
}) => {
  const { formatMessage } = useIntl()
  const formMethods = useForm<ClassificationRulesFormData>({ mode: 'onChange', shouldUnregister: false })
  const { mutateAsync: saveAsync, isPending: isSaving } = useCreateResource<
    QuestionAnswerClassificationRule[],
    (QuestionAnswerClassificationRule | undefined)[]
  >()
  const { account } = useAccount()
  const isWorldfavorOrganization = account?.organization?.id === WF_ORGANIZATION_ID
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const queryClient = useQueryClient()

  const originalClassificationRulesRef = useRef<ClassificationRulesFormData | null>(null)

  useEffect(() => {
    if (currentQuestion) {
      const resetData: ClassificationRulesFormData = {
        questionAnswerClassificationRules: mapToAnswerClassificationDataFormData(
          currentQuestion.questionAnswerClassificationRules,
        ),
        questionNumberClassificationRules: mapToNumberClassificationDataFormData(
          currentQuestion.questionAnswerClassificationRules,
        ),
        questionOptionClassificationRules: mapToOptionClassificationDataFormData(
          currentQuestion.questionAnswerClassificationRules,
        ),
        requestCollectionId: currentQuestion.requestCollectionId,
      }

      if (assessmentType === AssessmentType.Flexible && currentQuestion.assessmentData) {
        const existingData = currentQuestion.assessmentData
        resetData.impactValueType = existingData.impactValueType
        resetData.points = existingData.points
      }

      originalClassificationRulesRef.current = {
        questionAnswerClassificationRules: resetData.questionAnswerClassificationRules,
        questionNumberClassificationRules: resetData.questionNumberClassificationRules,
        questionOptionClassificationRules: resetData.questionOptionClassificationRules,
      }

      formMethods.reset(resetData)
    }
  }, [currentQuestion, assessmentType])

  const onSubmit = async (values: EditQuestionFormData) => {
    const currentClassificationRules = {
      questionAnswerClassificationRules: values.questionAnswerClassificationRules,
      questionNumberClassificationRules: values.questionNumberClassificationRules,
      questionOptionClassificationRules: values.questionOptionClassificationRules,
    }
    if (assessmentType === AssessmentType.Flexible && onClassificationSave && currentQuestion) {
      const formValues = formMethods.getValues()
      const classificationData: ClassificationRulesFormData = {
        ...currentClassificationRules,
        impactValueType: formValues.impactValueType,
        points: formValues.points,
      }
      onClassificationSave(currentQuestion.id, classificationData)
    }

    const shouldSaveClassificationRules =
      originalClassificationRulesRef.current &&
      hasClassificationRulesChanged(originalClassificationRulesRef.current, currentClassificationRules)

    if (shouldSaveClassificationRules) {
      await saveAsync(
        {
          url: endpoints.questionAnswerClassificationRule,
          body: mapToClassificationSubmitData(isWorldfavorOrganization, values)
            .filter(i => !!i)
            .map(i => ({
              ...i,
              questionId: currentQuestion?.id,
            })),
        },
        {
          onSuccess: async () => {
            showSnackbar({
              message: formatMessage(
                { id: 'templateBuilder.success' },
                { b: (chunks: React.ReactNode) => <b>{chunks}</b>, templateName: values.questionText },
              ),
              severity: 'success',
            })
            queryClient.invalidateQueries({ queryKey: [FetchKey.OrganizationOwnedTemplates] })
            queryClient.invalidateQueries({ queryKey: [FetchKey.AssessmentTemplateWithClassificationRules] })
          },
          onError: error => {
            showErrorNotification({ requestError: error })
          },
        },
      )
    }
    closeDialog()
  }

  if (!currentQuestion) return null

  return (
    <FormProvider {...formMethods}>
      <Dialog
        maxWidth="sm"
        onFormSubmit={formMethods.handleSubmit(onSubmit)}
        open={isOpen}
        onClose={() => {
          setCurrentQuestion(undefined)
          closeDialog()
        }}
        buttons={[
          { onClick: () => closeDialog(), label: formatMessage({ id: 'general.cancel' }), disabled: isSaving },
          {
            type: 'submit',
            variant: 'contained',
            label: formatMessage({ id: 'general.update' }),
            disabled: isSaving,
            loading: isSaving,
          },
        ]}
        title={formatMessage({ id: 'assessments.editClassification' })}
        content={
          <EditQuestionAnswerClassification
            questionTypeId={currentQuestion.questionTypeId}
            assessmentType={assessmentType}
          />
        }
      />
    </FormProvider>
  )
}
